import { Link, useParams } from "react-router";

import { useNavigation } from "@/providers";
import { type TriggerV2Record } from "@/shared/domain/triggers";
import {
  useTriggerV2,
  useUpdateTriggerV2,
} from "@/shared/domain/triggers/hooks";
import { Spinner } from "@/shared/foundation/primitives";

import { isEditableInForm } from "../TriggerForm/formScope";
import { TriggerForm } from "../TriggerForm/TriggerForm";
import { TriggerFormPage } from "../TriggerForm/TriggerFormPage";

import styles from "./EditTrigger.module.css";

/**
 * Edit one stored trigger on the same page it was created on.
 *
 * A trigger the form has no controls for is turned away rather than opened
 * half-represented: saving such a form would quietly rewrite the parts that
 * were never shown.
 */
export function EditTrigger() {
  const { triggerName } = useParams<{ triggerName: string }>();
  const triggerQuery = useTriggerV2(triggerName);

  return (
    <TriggerFormPage
      title="Edit trigger"
      subtitle={
        "Change what this trigger watches, how often it fires, and what it runs."
      }
      crumbs={
        triggerName !== undefined && (
          <Link to={detailsPath(triggerName)}>{triggerName}</Link>
        )
      }
    >
      {triggerQuery.isLoading && <Spinner />}
      {triggerQuery.error !== null && (
        <p
          className={styles.notice}
          data-testid="edit-trigger-error"
          role="alert"
        >
          {triggerQuery.error.message}
        </p>
      )}
      {triggerQuery.data !== undefined && <Editor record={triggerQuery.data} />}
    </TriggerFormPage>
  );
}

function detailsPath(triggerName: string): string {
  return `/triggers/${encodeURIComponent(triggerName)}`;
}

function Editor({ record }: { record: TriggerV2Record }) {
  const { mutateAsync: updateTrigger } = useUpdateTriggerV2();
  const { goto } = useNavigation();
  const backToTrigger = () => goto.trigger({ triggerId: record.name });

  if (!isEditableInForm(record)) {
    return (
      <div className={styles.notice} data-testid="edit-trigger-unavailable">
        <p>
          This trigger watches something the form cannot show, so editing it
          here would drop what is not on the page. It can still be enabled,
          disabled, and deleted from its own page.
        </p>
        <Link to={detailsPath(record.name)}>Back to {record.name}</Link>
      </div>
    );
  }

  return (
    <TriggerForm
      mode="edit"
      initial={record}
      onSubmit={async (request) => {
        // The name is the trigger's identifier and the update endpoint has no
        // field for it; everything else the builder produces is an update.
        const { name: _name, ...updates } = request;
        await updateTrigger({ name: record.name, updates });
        backToTrigger();
      }}
      onCancel={backToTrigger}
    />
  );
}
